import React, { useContext, useEffect, useState } from "react";
import { generateRandomId } from "../../../core/commonFun";
import { AppContext } from "../../../../context";
import { useSnackbar } from "notistack";

// Components
import AddMedicationSkeleton from "../Skeletons/AddMedicationSkeleton";

// API
import {
  HandleBlurForAssistant,
  fetchRecommendedMedicines,
} from "../ClinicalNotesAPI/ClinicalNoteAPIManager";

// Dropdown data
import {
  dosageForm,
  frequencyList,
  routeList,
  takenWithList,
} from "../ClinicalNotesAPI/DropdownData";

// MUI
import {
  Autocomplete,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import MedicationAddDialog from "../Dialogs/MedicationAddDialog";
import { deepEqual, isDeepEqual } from "../lib/utils";

function PrescriptionMedicineUpdate() {
  const {
    expertClinicalNotesData,
    setExpertClinicalNotesData,
    savedCopy,
    setSavedCopy,
  } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [recommendedMedicines, setRecommendedMedicines] = useState([]);
  const [sendData, setSendData] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("");
  const [openDialog, setOpenDialog] = useState({ open: false, data: null });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    if (
      Object.keys(expertClinicalNotesData?.data?.progress_note || {}).length > 0
    ) {
      setIsLoading(false);
    }
  }, [expertClinicalNotesData?.data?.progress_note]);

  useEffect(() => {
    if (
      expertClinicalNotesData?.headerData?.session_id &&
      !expertClinicalNotesData?.data?.submitted &&
      expertClinicalNotesData?.data?.progress_note?.medicines
        ?.prescribed_medicines?.length > 0
    ) {
      handleBlur();
    }
  }, [
    expertClinicalNotesData?.data?.progress_note?.medicines
      ?.prescribed_medicines?.length,
  ]);

  useEffect(() => {
    if (sendData) {
      const data = JSON.stringify({
        header: expertClinicalNotesData?.headerData,
        session_data: {
          ...expertClinicalNotesData?.data,
          progress_note: {
            ...expertClinicalNotesData?.data?.progress_note,
            medicines: {
              ...expertClinicalNotesData?.data?.progress_note?.medicines,
              prescribed_medicines:
                expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
                  (item) =>
                    item?.drug !== "" &&
                    item?.drug != null &&
                    item?.drug !== undefined
                ),
            },
          },
          lab_test: {
            ...expertClinicalNotesData?.data?.lab_test,
            prescribed_lab_test:
              expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test?.filter(
                (item) =>
                  item?.test_name !== "" &&
                  item?.test_name != null &&
                  item?.test_name !== undefined
              ),
          },
        },
      });
      expertClinicalNotesData?.socketRef?.current?.send(data);
      setExpertClinicalNotesData({
        ...expertClinicalNotesData,
        data: {
          ...expertClinicalNotesData?.data,
          progress_note: {
            ...expertClinicalNotesData?.data?.progress_note,
            medicines: {
              ...expertClinicalNotesData?.data?.progress_note?.medicines,
              prescribed_medicines:
                expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
                  (item) =>
                    item?.drug !== "" &&
                    item?.drug != null &&
                    item?.drug !== undefined
                ),
            },
          },
          lab_test: {
            ...expertClinicalNotesData?.data?.lab_test,
            prescribed_lab_test:
              expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test?.filter(
                (item) =>
                  item?.test_name !== "" &&
                  item?.test_name != null &&
                  item?.test_name !== undefined
              ),
          },
        },
      });
      setSendData(false);
    }
  }, [sendData]);

  useEffect(() => {
    if (!searchTerm) return;
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm && searchType === "medicine") {
      fetchRecommendedMedicines(debouncedSearchTerm)
        .then((response) => {
          if (response.status === "success") {
            setRecommendedMedicines(response?.data);
          } else {
            setRecommendedMedicines([]);
          }
        })
        .catch((err) => {
          console.error(err); // Optionally log the error internally here as well
        });
    }
  }, [debouncedSearchTerm]);

  const handleDeleteRow = (rowIndex, row) => {
    const updatedPrescribedMedicines =
      expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
        (_, index) => index !== rowIndex
      );
      const list = new Set(savedCopy?.list);

      if (
        !isDeepEqual(
          updatedPrescribedMedicines,
          savedCopy["progress_note"]?.medicines?.prescribed_medicines
        )
      ) {
        list.add("prescribed_medicines");
        setSavedCopy((prevState) => ({
          ...prevState,
          list,
        }));
        console.log(list);
      } else {
        list.delete("prescribed_medicines");
        setSavedCopy((prevState) => ({
          ...prevState,
          list,
        }));
      }
    setExpertClinicalNotesData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        progress_note: {
          ...(prevData.data.progress_note || {}),
          medicines: {
            ...(prevData.data.progress_note.medicines || {}),
            prescribed_medicines: updatedPrescribedMedicines,
          },
        },
      },
    }));
    expertClinicalNotesData.socketRef.current.send(
      JSON.stringify({
        action_type: "medication_deleted",
        details: {
          medication_name: row?.drug,
        },
        user_id: expertClinicalNotesData?.headerData?.user_id,
        expert_id: expertClinicalNotesData?.headerData?.expert_id,
      })
    );
    handleBlur();
  };

  const handleInputChange = (rowIndex, field, value, cims) => {
    setRecommendedMedicines([]);
    const updatedRows =
      expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.map(
        (row, index) =>
          index === rowIndex
            ? {
                ...row,
                [field]: value,
                cims: cims?.length > 0 ? cims?.split(",") : row?.cims,
              }
            : row
      );
    if (field === "drug") {
      setSearchTerm(value);
      setSearchType("medicine");
    }
    setExpertClinicalNotesData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        progress_note: {
          ...(prevData.data.progress_note || {}),
          medicines: {
            ...(prevData.data.progress_note.medicines || {}),
            prescribed_medicines: updatedRows,
          },
        },
      },
    }));
  };
  const handleBlur = async () => {
    const payload = {
      ...expertClinicalNotesData?.headerData,
      assistant: {},
      drugs:
        expertClinicalNotesData?.data?.progress_note?.medicines
          ?.prescribed_medicines,
      labtest: expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test,
      disease: "",
    };

    HandleBlurForAssistant(payload)
      .then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.message, { variant: "info" });
          return;
        }
      })
      .catch((err) => {
        console.error(err); // Optionally log the error internally here as well
      });
    setSendData(true);
  };

  const handleAddRow = (newRow) => {
    // Create a copy of the prescribed medicines array or initialize it as an empty array
    const updatedPrescribedMedicines = [
      ...(expertClinicalNotesData?.data?.progress_note?.medicines
        ?.prescribed_medicines || []),
      newRow,
    ];

    const list = new Set(savedCopy?.list);

    if (
      !isDeepEqual(
        updatedPrescribedMedicines,
        savedCopy["progress_note"]?.medicines?.prescribed_medicines
      )
    ) {
      list.add("prescribed_medicines");
      setSavedCopy((prevState) => ({
        ...prevState,
        list,
      }));
      console.log(list);
    } else {
      list.delete("prescribed_medicines");
      setSavedCopy((prevState) => ({
        ...prevState,
        list,
      }));
    }

    // Update the state immutably
    setExpertClinicalNotesData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        progress_note: {
          ...prevData.data.progress_note,
          medicines: {
            ...(prevData.data.progress_note?.medicines || {}),
            prescribed_medicines: updatedPrescribedMedicines,
          },
        },
      },
    }));

    // Send the updated information through the socket
    expertClinicalNotesData?.socketRef?.current?.send(
      JSON.stringify({
        action_type: "medication_added",
        details: {
          medication_name: newRow?.drug,
        },
        user_id: expertClinicalNotesData?.headerData?.user_id,
        expert_id: expertClinicalNotesData?.headerData?.expert_id,
      })
    );

    // Call handleBlur if defined
    handleBlur();
  };

  const handleEditRow = (rowIndex, data) => {
    const updatedPrescribedMedicines =
      expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.map(
        (med, index) => (index === rowIndex - 1 ? (med = data) : med)
      );

    const list = new Set(savedCopy?.list);

    if (
      !isDeepEqual(
        updatedPrescribedMedicines,
        savedCopy["progress_note"]?.medicines?.prescribed_medicines
      )
    ) {
      list.add("prescribed_medicines");
      setSavedCopy((prevState) => ({
        ...prevState,
        list,
      }));
      console.log(list);
    } else {
      list.delete("prescribed_medicines");
      setSavedCopy((prevState) => ({
        ...prevState,
        list,
      }));
    }

    setExpertClinicalNotesData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        progress_note: {
          ...(prevData.data.progress_note || {}),
          medicines: {
            ...(prevData.data.progress_note.medicines || {}),
            prescribed_medicines: updatedPrescribedMedicines,
          },
        },
      },
    }));
    handleBlur();
  };

  return (
    <div style={{ overflowX: "auto" }} className="p-lg-2 ">
      {isLoading ? (
        <AddMedicationSkeleton />
      ) : (
        <div>
          {expertClinicalNotesData?.data?.progress_note?.medicines
            ?.prescribed_medicines?.length > 0 && (
            <TableContainer>
              <Table aria-label="medication table">
                <TableHead>
                  <TableRow>
                    <TableCell>Sl.No</TableCell>
                    <TableCell>Medication Details</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines.map(
                    (row, index) => (
                      <TableRow hover key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              row?.cims?.length > 0 && (
                                <div className="d-flex flex-wrap gap-2">
                                  {row?.cims?.map((item, idx) => (
                                    <span
                                      key={idx}
                                      style={{
                                        fontSize: "12px",
                                        background: "#DFF5D6",
                                      }}
                                      className="rounded-pill px-2 py-1"
                                    >
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              )
                            }
                            placement="top-start"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "common.white",
                                  "& .MuiTooltip-arrow": {
                                    color: "common.white",
                                  },
                                  color: "common.black",
                                  padding: "10px",
                                },
                              },
                            }}
                          >
                            {`${row?.drug || ""} ${row?.strength || ""} ${
                              row?.dosage_form || ""
                            }, ${row?.quantity || ""} to be Taken ${
                              row?.route || ""
                            } ${row?.frequency || ""}${
                              row?.start_date ? ` from ${row?.start_date}` : ""
                            }${row?.end_date ? ` until ${row?.end_date}` : ""}${
                              row?.dispensation
                                ? `, Dispense ${row?.dispensation}`
                                : ""
                            }${
                              row?.refills
                                ? ` with ${row?.refills} Repeat(s)`
                                : ""
                            }${
                              row?.instructions ? `. ${row?.instructions}` : "."
                            }`}
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {!expertClinicalNotesData?.data?.submitted && (
                            <div className="d-flex align-items-center gap-1">
                              <IconButton
                                sx={{ minWidth: "max-content" }}
                                onClick={() =>
                                  setOpenDialog({
                                    open: true,
                                    data: row,
                                    index: index + 1,
                                  })
                                }
                                disabled={
                                  expertClinicalNotesData?.data?.submitted ||
                                  expertClinicalNotesData?.editTemplate
                                }
                              >
                                <Edit className="text-primary" />
                              </IconButton>
                              <IconButton
                                sx={{ minWidth: "max-content" }}
                                onClick={() => handleDeleteRow(index, row)}
                                disabled={
                                  expertClinicalNotesData?.data?.submitted ||
                                  expertClinicalNotesData?.editTemplate
                                }
                              >
                                <Delete className="text-danger" />
                              </IconButton>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <MedicationAddDialog
            openDialog={openDialog.open}
            closeDialog={() => setOpenDialog({ open: false, data: null })}
            handleData={(data) => handleAddRow(data)}
            label={"Add Medication"}
            data={openDialog.data}
            index={openDialog?.index}
            handleEdit={(id, data) => handleEditRow(id, data)}
          />
          {!expertClinicalNotesData?.data?.submitted && (
            <Button
              onClick={() => setOpenDialog({ open: true, data: null })}
              sx={{ marginTop: "20px" }}
              className="PrimaryCTA"
              disabled={
                expertClinicalNotesData?.data?.submitted ||
                expertClinicalNotesData?.editTemplate
              }
              id="add_new_medication"
            >
              Add New Medicine
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default PrescriptionMedicineUpdate;
